import { useContext, useMemo } from 'react'
import { useStreamApiV2 } from 'domains/streamApi'
import { DateRangeContext } from 'ui/contexts/DateRangeContext'

/**
 * Convert columnar stream data to row format
 * @param {object} data - Columnar data where each field is an array
 * @returns {array} Array of row objects
 */
const columnarToRows = (data) => {
  if (!data || typeof data !== 'object') return []

  const keys = Object.keys(data)
  if (keys.length === 0) return []

  const [firstKey] = keys
  const arrayLength = Array.isArray(data[firstKey]) ? data[firstKey].length : 0

  if (arrayLength === 0) return []

  const rows = []
  for (let i = 0; i < arrayLength; i++) {
    const row = {}
    keys.forEach((key) => {
      row[key] = data[key][i]
    })
    rows.push(row)
  }

  return rows
}

/**
 * Format tap count display
 * @param {object} event - The tap test event object
 * @returns {string|null} Formatted tap count string or null
 */
const formatTapCount = (event) => {
  if (!event?.tap_count) return null
  const { correct, incorrect, all } = event.tap_count
  return `${all} taps (${correct} correct${
    incorrect ? `, ${incorrect} error${incorrect > 1 ? 's' : ''}` : ''
  })`
}

/**
 * Get tap test data for a patient within a date range
 * @param {string} patientId - The patient's ID
 * @param {number} startTime - Start time as Unix timestamp (seconds)
 * @param {number} endTime - End time as Unix timestamp (seconds)
 * @return {{loading, error, tapTests}} loading is the loading state, error is the error state, tapTests is an array of tap test events
 */
export const useTapTestData = (patientId, startTime, endTime) => {
  const { selectedTimezone } = useContext(DateRangeContext)

  const filters = {
    patientId,
    algorithm: 'ingest-strive-taptest.0',
    deviceId: 'all',
    streamTypeId: 'span',
    parameters: [
      { key: 'category', value: 'assessment' },
      { key: 'measurement', value: 'statistics' },
    ],
  }

  const params = {
    start_time: startTime,
    end_time: endTime,
    timezone_name: selectedTimezone,
  }

  const { data, loading, errors } = useStreamApiV2({
    filters,
    params,
    skip: !patientId || !startTime || !endTime,
  })

  // Transform stream data into event-like objects
  const tapTests = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    // Collect all points from all streams
    const allPoints = []
    data.forEach((streamResult) => {
      const points = columnarToRows(streamResult.data)

      points.forEach((point) => {
        const event = point.event || {}
        if (event.test_status === 'completed') {
          allPoints.push({
            event,
            testGroupId: event.test_group_id,
            startTime: new Date(point.start_time).getTime() / 1000,
            endTime: new Date(point.end_time).getTime() / 1000,
          })
        }
      })
    })

    // Deduplicate by (testGroupId, hand_tested)
    const seenKeys = new Set()
    const dedupedPoints = allPoints.filter((point) => {
      const key = `${point.testGroupId}-${point.event.hand_tested}`
      if (seenKeys.has(key)) {
        return false
      }
      seenKeys.add(key)
      return true
    })

    // Group by test_group_id to combine left/right hand tests
    const groupedBySession = dedupedPoints.reduce((acc, point) => {
      const key = point.testGroupId
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(point)
      return acc
    }, {})

    // Create one event per session
    const events = Object.entries(groupedBySession).map(
      ([testGroupId, sessionPoints]) => {
        sessionPoints.sort((a, b) => a.startTime - b.startTime)

        const earliestStart = sessionPoints[0].startTime
        const latestEnd = sessionPoints[sessionPoints.length - 1].endTime

        const handDetails = sessionPoints.map((p) => {
          const hand = p.event.hand_tested
          const tapInfo = formatTapCount(p.event)
          return `${
            hand ? hand.charAt(0).toUpperCase() + hand.slice(1) : 'Unknown'
          }: ${tapInfo || 'N/A'}`
        })

        return {
          id: `${testGroupId}-${earliestStart}`,
          classification: {
            id: 'tap-test',
            namespace: 'system',
            category: 'tap-test',
            enum: 'tap-test',
          },
          displayName: handDetails.join(' | '),
          duration: {
            startTime: earliestStart,
            endTime: latestEnd,
          },
          ongoing: false,
          payload: JSON.stringify({
            testGroupId,
            hands: sessionPoints.map((p) => ({
              hand: p.event.hand_tested,
              tapCount: p.event.tap_count,
              errorRate: p.event.error_rate,
            })),
          }),
        }
      },
    )

    return events.sort((a, b) => b.duration.startTime - a.duration.startTime)
  }, [data])

  return {
    loading,
    error: errors?.length > 0 ? errors[0] : null,
    tapTests,
  }
}

export default useTapTestData
